import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import type { Material } from "@/hooks/useBioNexusData";
import type { TablesUpdate } from "@/integrations/supabase/types";
import { cleanTranslations, TranslationField, TranslationFields } from "./TranslationFields";

const TRANSLATION_FIELDS: TranslationField[] = [
  { locale: "zh", key: "description", label: "Description", type: "textarea" },
  { locale: "zh", key: "primary_application", label: "Primary use", type: "text" },
  { locale: "zh", key: "fabrication_method", label: "Fabrication method", type: "textarea" },
  { locale: "it", key: "name", label: "Name", type: "text" },
  { locale: "it", key: "description", label: "Description", type: "textarea" },
  { locale: "it", key: "primary_application", label: "Primary use", type: "text" },
  { locale: "it", key: "fabrication_method", label: "Fabrication method", type: "textarea" },
];

interface MaterialEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  material: Material | null;
  onSave: (id: string, updates: TablesUpdate<"materials">) => void;
  isSaving?: boolean;
}

export const MaterialEditDialog = ({
  open,
  onOpenChange,
  material,
  onSave,
  isSaving,
}: MaterialEditDialogProps) => {
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (material) {
      setFormData({ ...material });
    }
  }, [material]);

  const updateField = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateImageUrl = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      image_url: value,
      image_credit: "",
      image_source_url: "",
    }));
  };

  const handleSave = () => {
    if (!material) return;
    const updates: TablesUpdate<"materials"> = {
      name: formData.name as string,
      name_cn: formData.name_cn as string,
      family: formData.family as string,
      description: formData.description as string,
      source_paper: formData.source_paper as string,
      source_doi: formData.source_doi as string,
      source_url: formData.source_url as string,
      paper_abstract: formData.paper_abstract as string,
      degradation_value: formData.degradation_value as string,
      degradation_source: formData.degradation_source as string,
      degradation_method: formData.degradation_method as string,
      tensile_strength_mpa: formData.tensile_strength_mpa as number,
      tensile_source: formData.tensile_source as string,
      electrical_performance: formData.electrical_performance as string,
      electrical_source: formData.electrical_source as string,
      water_resistance: formData.water_resistance as string,
      water_resistance_source: formData.water_resistance_source as string,
      cost_per_kg: formData.cost_per_kg as number,
      cost_source: formData.cost_source as string,
      fabrication_method: formData.fabrication_method as string,
      fabrication_source: formData.fabrication_source as string,
      image_url: (formData.image_url as string) || null,
      image_credit: (formData.image_credit as string) || null,
      image_source_url: (formData.image_source_url as string) || null,
      published: formData.published as boolean,
      translations: cleanTranslations(formData.translations),
    };
    onSave(material.id, updates);
  };

  const fields = [
    { key: "name", label: "Name (EN)", type: "text" },
    { key: "name_cn", label: "Name (CN)", type: "text" },
    { key: "family", label: "Family", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "source_paper", label: "Paper Citation", type: "text" },
    { key: "source_doi", label: "DOI", type: "text" },
    { key: "source_url", label: "Source URL", type: "text" },
    { key: "paper_abstract", label: "Abstract", type: "textarea" },
    { key: "degradation_value", label: "Degradation Value", type: "text" },
    { key: "degradation_source", label: "Degradation Source", type: "text" },
    { key: "degradation_method", label: "Degradation Method", type: "text" },
    { key: "tensile_strength_mpa", label: "Tensile Strength (MPa)", type: "number" },
    { key: "tensile_source", label: "Tensile Source", type: "text" },
    { key: "electrical_performance", label: "Electrical Performance", type: "text" },
    { key: "electrical_source", label: "Electrical Source", type: "text" },
    { key: "water_resistance", label: "Water Resistance", type: "text" },
    { key: "water_resistance_source", label: "Water Resistance Source", type: "text" },
    { key: "cost_per_kg", label: "Cost per kg", type: "number" },
    { key: "cost_source", label: "Cost Source", type: "text" },
    { key: "fabrication_method", label: "Fabrication Method", type: "text" },
    { key: "fabrication_source", label: "Fabrication Source", type: "text" },
    { key: "image_url", label: "Image URL", type: "text" },
    { key: "image_credit", label: "Image Credit", type: "text" },
    { key: "image_source_url", label: "Image Source URL", type: "text" },
  ];

  if (!material) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-display">
            Edit Material: {(formData.name as string) || material.name}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4">
            {fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key} className="font-mono text-xs uppercase">
                  {field.label}
                </Label>
                {field.type === "textarea" ? (
                  <Textarea
                    id={field.key}
                    value={(formData[field.key] as string) || ""}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    rows={3}
                  />
                ) : field.type === "number" ? (
                  <Input
                    id={field.key}
                    type="number"
                    value={(formData[field.key] as number) ?? ""}
                    onChange={(e) => updateField(field.key, e.target.value ? Number(e.target.value) : null)}
                  />
                ) : (
                  <Input
                    id={field.key}
                    value={(formData[field.key] as string) || ""}
                    onChange={(e) => field.key === "image_url"
                      ? updateImageUrl(e.target.value)
                      : updateField(field.key, e.target.value)}
                  />
                )}
              </div>
            ))}

            <TranslationFields
              fields={TRANSLATION_FIELDS}
              value={formData.translations}
              onChange={(next) => updateField("translations", next)}
            />

            <div className="flex items-center gap-3 pt-4 border-t border-border">
              <Checkbox
                id="published"
                checked={(formData.published as boolean) ?? true}
                onCheckedChange={(checked) => updateField("published", checked)}
              />
              <Label htmlFor="published" className="font-mono text-sm">
                Published — Show on website
              </Label>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
